import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import firebase from 'firebase/app';

import { myProfile } from 'Redux/user-profile/selectors';

import { fetchCurrentUserToken, fetchCreateCustomToken } from './AuthenticationConnector';

const AuthFirebaseContext = React.createContext();

const LOGIN_STATE = {
  LOADING: 'LOADING',
  LOGGED_IN: 'LOGGED_IN',
  NOT_LOGGED_IN: 'NOT_LOGGED_IN',
};

const BaseFirebaseAuthenticationProvider = ({ children, systemUser }) => {
  const [loginState, setLoginState] = useState(LOGIN_STATE.NOT_LOGGED_IN);
  const [user, setUser] = useState(null);

  const login = async () => {
    if (firebase.apps.length) {
      try {
        setLoginState(LOGIN_STATE.LOADING);
        const token = await fetchCurrentUserToken();
        const firebaseAuthToken = await fetchCreateCustomToken(token);
        await firebase.auth().signInWithCustomToken(firebaseAuthToken);
        setLoginState(LOGIN_STATE.LOGGED_IN);
      } catch (err) {
        setLoginState(LOGIN_STATE.NOT_LOGGED_IN);
        console.error(err);
      }
    }
  };

  const logout = async () => {
    if (firebase.apps.length) {
      try {
        setLoginState(LOGIN_STATE.LOADING);
        await firebase.auth().signOut();
        setLoginState(LOGIN_STATE.NOT_LOGGED_IN);
      } catch (err) {
        setLoginState(LOGIN_STATE.NOT_LOGGED_IN);
        console.error(err);
      }
    }
  };

  useEffect(() => {
    if (firebase.apps.length) {
      /**
       * After each time the subscription function is run, always set loading to false.
       */
      const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
        if (user) {
          setUser({ id: user.uid, ...user });
        } else {
          setUser(null);
        }
      });
      return () => {
        unsubscribe();
      };
    }
  }, []);

  useEffect(() => {
    // assume: system user and firebase user have same id
    if (systemUser && (loginState === LOGIN_STATE.NOT_LOGGED_IN || systemUser.id !== user?.id)) {
      login();
    }
    if (!systemUser && loginState === LOGIN_STATE.LOGGED_IN) {
      logout();
    }
  }, [systemUser]);

  return (
    <AuthFirebaseContext.Provider
      value={{ loginState, user, isFirebaseInitialize: firebase.apps.length !== 0 }}
    >
      {children}
    </AuthFirebaseContext.Provider>
  );
};

const FirebaseAuthenticationProvider = connect((state) => {
  const user = myProfile(state);
  return { systemUser: user };
}, {})(BaseFirebaseAuthenticationProvider);

const useAuthenticationFirebase = () => {
  const context = useContext(AuthFirebaseContext);
  if (context === undefined) {
    throw new Error(
      'useAuthenticationFirebase must be used within a FirebaseAuthenticationProvider'
    );
  }
  return context;
};

export { FirebaseAuthenticationProvider, useAuthenticationFirebase, LOGIN_STATE };
